import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { IAccEligibility, IAccEligibilityResult, IAccServiceCode, ISaveAccEligibility } from '../../models/acc-elgibility.model';
import { HttpClient } from '@angular/common/http';
import { ConsultationRecommendationService } from '../consultation-recommendation/consultation-recommendation.service';
import { RecommendationType } from '../../models/consultation-recommendation.model';
import { environment } from '../../../environments/environment';
import { API_ROUTES } from '../../util/route.constants';

const STORAGE_KEY = 'recommendedAccEligibility';
const STORAGE_KEY_SERVICE_CODE = 'recommendedAccServiceCodes';

@Injectable({
  providedIn: 'root',
})
export class AccEligibilityService {
  private readonly accElgibilitySubject = new BehaviorSubject<IAccEligibility[]>([]);
  accElgibility$ = this.accElgibilitySubject.asObservable();

  private readonly accServiceCodeSubject = new BehaviorSubject<IAccServiceCode[]>([]);
  accServiceCode$ = this.accServiceCodeSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly injector: Injector
  ) {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      this.accElgibilitySubject.next(JSON.parse(stored));
    }

    const storedServiceCodes = localStorage.getItem(STORAGE_KEY_SERVICE_CODE);
    if (storedServiceCodes) {
      this.accServiceCodeSubject.next(JSON.parse(storedServiceCodes));
    }
  }

  notifyOpenDrawer() {
    //to avoid the circular dependency, using injector
    const consultationRecommendationService = this.injector.get(ConsultationRecommendationService);
    consultationRecommendationService.openDrawer(RecommendationType.ACC_ELIGIBILITY);
  }

  setAccEligibility(accElgibility: IAccEligibility[], isDataFromService = false) {
    this.accElgibilitySubject.next(accElgibility);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accElgibility));

    if (isDataFromService && accElgibility.length) {
      this.notifyOpenDrawer();
    }
  }

  toggleItem(index: number) {
    const items = [...this.accElgibilitySubject.value];
    items[index].checked = !items[index].checked;
    this.setAccEligibility(items);
  }

  setAccServiceCode(items: IAccServiceCode[], notifyDrawer = false) {
    this.accServiceCodeSubject.next(items);
    localStorage.setItem(STORAGE_KEY_SERVICE_CODE, JSON.stringify(items));

    if (notifyDrawer && items.length) {
      this.notifyOpenDrawer();
    }
  }

  toggleServiceItem(index: number) {
    const items = [...this.accServiceCodeSubject.value];
    if (!items[index]) return;
    items[index].checked = !items[index].checked;
    this.setAccServiceCode(items);
  }

  saveAccEligibility(payload: ISaveAccEligibility): Observable<IAccEligibilityResult> {
    const url = `${environment.cva_url}${API_ROUTES.TASK}${API_ROUTES.SAVE_ACC_ELIGIBILITY}`;
    return this.http.post<IAccEligibilityResult>(url, payload)
  }

  generateAccEligibility(
    transcript: string,
  ): Observable<{ response: IAccEligibility[] }> {
    return this.http.post<{ response: IAccEligibility[] }>(
      environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_ACC_ELIGIBILITY,
      { transcript }
    )
  }

  generateAccServiceCode(
    transcript: string,
    speechServiceTime: string,
    patientAge: string
  ): Observable<{ response: IAccServiceCode[] }> {
    const body = { transcript, patientAge, speechServiceTime };
    return this.http.post<IAccServiceCode[] | { response: IAccServiceCode[] }>(
      environment.cva_url + API_ROUTES.AI_PROXY + API_ROUTES.GENERATE_ACC_SERVICE_CODE,
      body
    ).pipe(
      // accept both a bare array and { response: [] }
      map(res => ({ response: Array.isArray(res) ? res : (res?.response ?? []) }))
    );
  }

  getAccEligibility() {
    return this.accElgibilitySubject.value;
  }

  clearAll() {
    this.setAccEligibility([]);
    this.setAccServiceCode([]);
  }

}
